import { useState, useEffect } from 'react'
import {
  Spinner, Alert, Table, Badge, Button, Modal, Form, Row, Col
} from 'react-bootstrap'
import client from '../../api/client'

function NovitaDeployments() {
  const [models, setModels] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [showAddModal, setShowAddModal] = useState(false)
  const [metadata, setMetadata] = useState({ categories: [], types: [] })
  const [formData, setFormData] = useState({
    name: '', code: '', category_id: '', type_id: '',
    cost_pt: 0, api_endpoint: '', vram_required: '', description: ''
  })
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    fetchModels()
    fetchMetadata()
  }, [])

  const fetchModels = async () => {
    try {
      const response = await client.get('/admin/api/v1/models?provider=novita')
      setModels(response.data.models || [])
    } catch (err) {
      setError('Erreur lors du chargement des déploiements Novita')
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  const fetchMetadata = async () => {
    try {
      const response = await client.get('/admin/api/v1/models/metadata')
      setMetadata(response.data)
    } catch (err) {
      console.error('Failed to fetch metadata:', err)
    }
  }

  const handleAdd = async (e) => {
    e.preventDefault()
    setSubmitting(true)
    try {
      await client.post('/admin/api/v1/models', {
        ...formData,
        provider: 'novita',
        api_provider: 'novita',
        is_self_hosted: true,
        active: true,
        cost_pt: parseInt(formData.cost_pt, 10) || 0,
      })
      setShowAddModal(false)
      resetForm()
      fetchModels()
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de la création')
    } finally {
      setSubmitting(false)
    }
  }

  const handleToggleActive = async (model) => {
    try {
      await client.patch(`/admin/api/v1/models/${model.id}`, {
        active: !model.active,
      })
      fetchModels()
    } catch (err) {
      setError('Erreur lors de la mise à jour')
    }
  }

  const handleDelete = async (model) => {
    if (!window.confirm(`Désactiver "${model.name}" ?`)) return
    try {
      await client.delete(`/admin/api/v1/models/${model.id}`)
      fetchModels()
    } catch (err) {
      setError('Erreur lors de la suppression')
    }
  }

  const resetForm = () => {
    setFormData({
      name: '', code: '', category_id: '', type_id: '',
      cost_pt: 0, api_endpoint: '', vram_required: '', description: ''
    })
  }

  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  if (loading) {
    return (
      <div className="d-flex justify-content-center p-5">
        <Spinner animation="border" variant="primary" />
      </div>
    )
  }

  const activeCount = models.filter(m => m.active).length

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1 className="h3 mb-0">Déploiements Novita</h1>
        <Button variant="primary" onClick={() => setShowAddModal(true)}>
          + Ajouter un déploiement
        </Button>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Row className="g-4 mb-4">
        <Col md={4}>
          <div className="bg-white p-3 rounded border">
            <div className="text-muted small">Total déploiements</div>
            <h3 className="mb-0">{models.length}</h3>
          </div>
        </Col>
        <Col md={4}>
          <div className="bg-white p-3 rounded border">
            <div className="text-muted small">Actifs</div>
            <h3 className="mb-0">{activeCount}</h3>
          </div>
        </Col>
        <Col md={4}>
          <div className="bg-white p-3 rounded border">
            <div className="text-muted small">Inactifs</div>
            <h3 className="mb-0">{models.length - activeCount}</h3>
          </div>
        </Col>
      </Row>

      {models.length === 0 ? (
        <Alert variant="info">Aucun déploiement Novita configuré</Alert>
      ) : (
        <Table responsive hover className="bg-white">
          <thead>
            <tr>
              <th>Modèle</th>
              <th>Type</th>
              <th>Coût (PT)</th>
              <th>Statut</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {models.map((model) => (
              <tr key={model.id}>
                <td>
                  <div className="fw-medium">{model.name}</div>
                  <small className="text-muted">{model.code}</small>
                </td>
                <td>
                  <Badge bg="secondary">{model.type_code || 'llm'}</Badge>
                </td>
                <td>{model.cost_pt}</td>
                <td>
                  <Badge bg={model.active ? 'success' : 'secondary'}>
                    {model.active ? 'Actif' : 'Inactif'}
                  </Badge>
                </td>
                <td>
                  <Button
                    variant={model.active ? 'outline-warning' : 'outline-success'}
                    size="sm"
                    className="me-2"
                    onClick={() => handleToggleActive(model)}
                  >
                    {model.active ? 'Désactiver' : 'Activer'}
                  </Button>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => handleDelete(model)}
                  >
                    Supprimer
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      <Modal show={showAddModal} onHide={() => setShowAddModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Nouveau déploiement Novita</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleAdd}>
          <Modal.Body>
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Nom</Form.Label>
                  <Form.Control
                    required
                    value={formData.name}
                    onChange={(e) => handleFormChange('name', e.target.value)}
                    placeholder="Ex: Llama 3.3 70B"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Code</Form.Label>
                  <Form.Control
                    required
                    value={formData.code}
                    onChange={(e) => handleFormChange('code', e.target.value)}
                    placeholder="Ex: meta-llama/llama-3.3-70b-instruct"
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Catégorie</Form.Label>
                  <Form.Select
                    required
                    value={formData.category_id}
                    onChange={(e) => handleFormChange('category_id', e.target.value)}
                  >
                    <option value="">Sélectionner...</option>
                    {metadata.categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.name} ({cat.code})
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Type</Form.Label>
                  <Form.Select
                    required
                    value={formData.type_id}
                    onChange={(e) => handleFormChange('type_id', e.target.value)}
                  >
                    <option value="">Sélectionner...</option>
                    {metadata.types.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name} ({t.code})
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Coût (PT)</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    required
                    value={formData.cost_pt}
                    onChange={(e) => handleFormChange('cost_pt', e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>VRAM requise</Form.Label>
                  <Form.Control
                    value={formData.vram_required}
                    onChange={(e) => handleFormChange('vram_required', e.target.value)}
                    placeholder="Ex: 80GB"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Endpoint API</Form.Label>
                  <Form.Control
                    value={formData.api_endpoint}
                    onChange={(e) => handleFormChange('api_endpoint', e.target.value)}
                    placeholder="https://api.novita.ai/v3/..."
                  />
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={formData.description}
                onChange={(e) => handleFormChange('description', e.target.value)}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowAddModal(false)}>
              Annuler
            </Button>
            <Button variant="primary" type="submit" disabled={submitting}>
              {submitting ? 'Création...' : 'Créer'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  )
}

export default NovitaDeployments
